import {
  Card,
  CardContent,
  Container,
  Grid2,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import AetherInventoryTable, { InventoryRow } from './AetherInventoryTable';
import { AetherInventoryUpdate } from '../../models/ManagementProduct';
import React, { useContext, useEffect, useMemo } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { WebstoreProductPart } from '../../models/Product';
import {
  useGetAetherInventoryQuery,
  useGetManagementProductQuery,
  useUpdateAetherInventoryMutation,
} from '../../redux/api/productApi';
import { useParams } from 'react-router-dom';
import { _useToasts } from '../../common/_useToasts';
import WholePageSpinner from '../../common/WholePageSpinner';
import _ from 'lodash';
import { FormContainer } from 'react-hook-form-mui';
import { LoadingButton } from '@mui/lab';
import MainBreadCrumb from '../../wrappers/breadcrumb/MainBreadCrumb';
import { AuthContext } from '../../common/Auth';
import { useThemeSettings } from '../../layouts/state/useThemeSettings';
import { newTheme } from '../../components/theme/theme';
import { getImageUrl } from '../../helpers/formattingHelpers';

interface AetherInventoryData {
  WebstoreProductPartArray: InventoryRow[];
}

export default function AetherInventoryPage() {
  const { id } = useParams<{ id: string }>();
  const { data: product, isLoading } = useGetManagementProductQuery({ id: id ?? '' });
  const [updateInventory, { isLoading: isLoadingUpdate }] = useUpdateAetherInventoryMutation();
  const { data: inventory } = useGetAetherInventoryQuery(id ?? '');
  const { _addToast } = _useToasts();

  const { settingData } = useContext(AuthContext);
  const { themedefaultColor, themedefaultTextColor, defaultFont } = useThemeSettings({
    settingData,
  });

  const parts: InventoryRow[] = useMemo(() => {
    const invMap = _.keyBy(inventory, 'sku');
    return (
      product?.WebstoreProductPartArray && product?.WebstoreProductPartArray.length > 0
        ? product?.WebstoreProductPartArray
        : product?.ProductPartArray.ProductPart ?? []
    ).map((part: WebstoreProductPart, index: number) => {
      const inv = invMap[part.sku];
      return {
        ...part,
        originalInventory: inv?.quantity ?? 0,
        aetherInventory: inv?.quantity ?? 0,
        aetherInventoryMinimum: inv?.minimum ?? 0,
        originalInventoryMinimum: inv?.minimum ?? 0,
        index,
      };
    });
  }, [product, inventory]);

  const formContext = useForm<AetherInventoryData>({
    defaultValues: useMemo(
      () => ({
        WebstoreProductPartArray: parts,
      }),
      [parts],
    ),
  });
  const { reset, handleSubmit, setValue } = formContext;

  useEffect(() => {
    reset({
      WebstoreProductPartArray: parts,
    });
  }, [parts, formContext, reset]);

  const formState = useFormState(formContext);

  const isDirty = useMemo(() => {
    return (
      formState.dirtyFields.WebstoreProductPartArray?.some(
        (part) => part.aetherInventory || part.aetherInventoryMinimum,
      ) ?? false
    );
  }, [formState]);

  const onSubmit = async (data: AetherInventoryData) => {
    const updateData: AetherInventoryUpdate = {
      id: id ?? '',
      parts: data.WebstoreProductPartArray.filter(
        (r) =>
          r.originalInventory !== r.aetherInventory ||
          r.originalInventoryMinimum !== r.aetherInventoryMinimum,
      ).map((p) => ({
        sku: p.sku,
        quantity: p.aetherInventory,
        minimum: p.aetherInventoryMinimum,
      })),
    };
    try {
      await updateInventory(updateData).unwrap();
      _addToast('Inventory updated successfully', {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      console.error(error);
      _addToast('Failed to update inventory', {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  };

  const imageUrl = getImageUrl(product?.MediaContent ?? []);

  return (
    <ThemeProvider
      theme={newTheme({ themedefaultColor, themedefaultTextColor, fontFamily: defaultFont })}
    >
      <MainBreadCrumb />
      <Container>
        <FormContainer formContext={formContext} handleSubmit={handleSubmit(onSubmit)}>
          {isLoading ? (
            <WholePageSpinner />
          ) : (
            <Stack spacing={2}>
              <Card>
                <CardContent>
                  <Grid2 container>
                    <Grid2 size={9}>
                      <Stack direction={'row'} spacing={2}>
                        {imageUrl && (
                          <img
                            src={imageUrl}
                            alt="Product"
                            style={{ maxWidth: 64, maxHeight: 64 }}
                          />
                        )}
                        <Stack direction={'column'}>
                          <Typography variant="h6">{product?.productName}</Typography>
                          <Typography variant="body2">Item #: {product?.productId}</Typography>
                          <Typography variant="body2">In House ID: {product?.inhouseId}</Typography>
                        </Stack>
                      </Stack>
                    </Grid2>
                    <Grid2 container size={3} alignItems={'center'}>
                      <LoadingButton
                        type={'submit'}
                        variant="contained"
                        loading={isLoadingUpdate}
                        disabled={!isDirty}
                        size="large"
                        fullWidth
                      >
                        Save
                      </LoadingButton>
                    </Grid2>
                  </Grid2>
                </CardContent>
              </Card>
              <AetherInventoryTable
                parts={parts}
                onChange={(index, row) => {
                  setValue(
                    `WebstoreProductPartArray.${index}.aetherInventory`,
                    row.aetherInventory ?? 0,
                    {
                      shouldDirty: true,
                    },
                  );
                  setValue(
                    `WebstoreProductPartArray.${index}.aetherInventoryMinimum`,
                    row.aetherInventoryMinimum ?? 0,
                    {
                      shouldDirty: true,
                    },
                  );
                }}
              />
            </Stack>
          )}
        </FormContainer>
      </Container>
    </ThemeProvider>
  );
}
